import type { WaitlistSubmitResult } from "./betaWaitlistCopy";
import {
  getExploreSignupContext,
  getOrCreateVisitorId,
  getSessionElapsedMs,
  getVisitorContext,
  hasSentEngagedVisit,
  markEngagedVisitSent,
  markModalAbandoned,
  takePostAbandonExploreModuleId,
  takeReturnAfterAbandon,
} from "./betaSessionMetrics";

export type WaitlistPayload = {
  email: string;
  name?: string;
  source?: string;
  website?: string;
  turnstileToken?: string;
};

export type ModalDismissMethod = "x" | "backdrop" | "not_now" | "escape";

export type BetaEventType =
  | "page_view"
  | "modal_open"
  | "module_zoom"
  | "join_click"
  | "founder_message_opened"
  | "founder_message_played"
  | "founder_message_paused"
  | "founder_message_stopped"
  | "founder_message_closed"
  | "founder_message_progress"
  | "founder_message_completed"
  | "founder_message_left"
  | "modal_abandon"
  | "engaged_visit"
  | "post_abandon_explore"
  | "return_after_abandon";

export type BetaEventPayload = {
  type: BetaEventType;
  source?: string;
  visitor_id?: string;
  is_returning?: boolean;
  module_id?: string;
  surface?: string;
  placement?: "nav" | "hero" | "gallery" | "finale";
  /** How the join form was closed — only on modal_abandon. */
  dismiss_method?: ModalDismissMethod;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  position_seconds?: number;
  listened_seconds?: number;
  duration_seconds?: number;
  percent_complete?: number;
  seconds_since_open?: number;
  milestone?: "25_percent" | "50_percent" | "60_seconds" | "75_percent";
  founder_message_opened?: boolean;
  within_5m_of_founder_message?: boolean;
  seconds_since_founder_message?: number;
  audio_position_seconds?: number;
  audio_listened_seconds?: number;
  audio_percent_complete?: number;
  audio_session_id?: string;
  founder_message_started?: boolean;
  founder_message_completed?: boolean;
};

function apiBase(): string {
  const url = import.meta.env.VITE_BETA_WAITLIST_URL;
  if (!url) {
    throw new Error("VITE_BETA_WAITLIST_URL is not configured");
  }
  return url.endsWith("/") ? url : `${url}/`;
}

const SESSION_METRICS_PREFIX = "rw-beta-metrics:";

function safeCampaignValue(value: string | null, fallback = ""): string {
  const cleaned = String(value || "").trim().replace(/[^a-zA-Z0-9_.-]/g, "-").slice(0, 80);
  return cleaned || fallback;
}

export function readCampaignAttribution(): Pick<
  BetaEventPayload,
  "source" | "utm_source" | "utm_medium" | "utm_campaign"
> {
  if (typeof window === "undefined") return { source: "beta-page" };
  const params = new URLSearchParams(window.location.search);
  const utmSource = safeCampaignValue(params.get("utm_source"));
  return {
    source: safeCampaignValue(utmSource || params.get("source"), "beta-page"),
    ...(utmSource ? { utm_source: utmSource } : {}),
    ...(safeCampaignValue(params.get("utm_medium"))
      ? { utm_medium: safeCampaignValue(params.get("utm_medium")) }
      : {}),
    ...(safeCampaignValue(params.get("utm_campaign"))
      ? { utm_campaign: safeCampaignValue(params.get("utm_campaign")) }
      : {}),
  };
}

/** Campaign source from explicit source or standard UTM attribution. */
export function readCampaignSource(): string {
  return readCampaignAttribution().source ?? "beta-page";
}

export function buildAppEntryUrl(kind: "sign-up" | "sign-in", placement: string): string {
  const url = new URL(`https://app.realmwarden.com/${kind}`);
  const visitorId = getOrCreateVisitorId();
  const attribution = readCampaignAttribution();
  url.searchParams.set("visitor_id", visitorId);
  url.searchParams.set("placement", safeCampaignValue(placement));
  Object.entries(attribution).forEach(([key, value]) => value && url.searchParams.set(key, value));
  return url.toString();
}

/**
 * Auto-open join modal ONLY for intentional homepage handoff
 * (`/beta/?source=homepage` after clicking Join on the slim root page).
 * Cold traffic — QR (`source=qr`), social, direct — must see the showcase first.
 */
export function shouldAutoOpenJoinModal(): boolean {
  return readCampaignSource() === "homepage";
}

function sessionMetricKey(type: string): string {
  return `${SESSION_METRICS_PREFIX}${type}`;
}

function shouldDedupeEvent(type: BetaEventType, payload: BetaEventPayload): string | null {
  if (
    type === "modal_open" ||
    type === "engaged_visit" ||
    type === "post_abandon_explore" ||
    type === "return_after_abandon"
  ) {
    return sessionMetricKey(type);
  }
  if (type === "join_click" && payload.placement) {
    return sessionMetricKey(`${type}:${payload.placement}`);
  }
  if (type === "module_zoom" && payload.module_id) {
    return sessionMetricKey(`${type}:${payload.module_id}`);
  }
  return null;
}

function buildBetaEventPayload(type: BetaEventType, extras: Partial<BetaEventPayload> = {}): BetaEventPayload {
  const attribution = readCampaignAttribution();
  const source = extras.source ?? attribution.source;
  if (type === "page_view") {
    // Prefer caller-provided visitor so we never call getVisitorContext twice
    // (second call would force is_returning=true after HAS_VISITED is set).
    if (typeof extras.is_returning === "boolean" && extras.visitor_id) {
      return {
        type,
        ...attribution,
        source,
        visitor_id: extras.visitor_id,
        is_returning: extras.is_returning,
      };
    }
    const visitor = getVisitorContext();
    return {
      type,
      ...attribution,
      source,
      visitor_id: visitor.visitorId,
      is_returning: visitor.isReturning,
    };
  }
  const visitor = getVisitorContext();
  return { type, ...attribution, source, visitor_id: visitor.visitorId, ...extras };
}

const BETA_METRICS_EXCLUDE_COOKIE = "rw-beta-metrics-exclude";
const BETA_METRICS_EXCLUDE_STORAGE_KEY = "rw-beta-metrics-exclude";

function hasExcludeCookie(): boolean {
  try {
    return document.cookie.split(";").some((part) => part.trim() === `${BETA_METRICS_EXCLUDE_COOKIE}=1`);
  } catch {
    return false;
  }
}

function hasExcludeStorage(): boolean {
  try {
    return localStorage.getItem(BETA_METRICS_EXCLUDE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function mirrorExcludeToStorage(): void {
  try {
    localStorage.setItem(BETA_METRICS_EXCLUDE_STORAGE_KEY, "1");
  } catch {
    /* storage unavailable */
  }
}

/**
 * Staff browsers (Bryan / Jared) that signed into waitlist-admin are excluded.
 * Cookie + same-origin localStorage mirror — covers Path=/ on realmwarden.com.
 * Must be a successful admin login (not merely visiting the login form).
 */
export function shouldExcludeBetaMetrics(): boolean {
  if (typeof document === "undefined") return false;
  if (hasExcludeCookie()) {
    mirrorExcludeToStorage();
    return true;
  }
  return hasExcludeStorage();
}

/** Fire-and-forget analytics beacon — session dedupe where applicable. */
export function recordBetaEvent(type: BetaEventType, extras: Partial<BetaEventPayload> = {}): void {
  const base = import.meta.env.VITE_BETA_WAITLIST_URL;
  if (!base || typeof window === "undefined") return;
  if (shouldExcludeBetaMetrics()) return;

  const payload = buildBetaEventPayload(type, extras);
  const dedupeKey = shouldDedupeEvent(type, payload);
  if (dedupeKey) {
    try {
      if (sessionStorage.getItem(dedupeKey) === "1") return;
      sessionStorage.setItem(dedupeKey, "1");
    } catch {
      /* sessionStorage unavailable */
    }
  }

  void fetch(`${base.endsWith("/") ? base : `${base}/`}event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    /* non-blocking */
  });
}

/** First exploration signal (module zoom or join click) — once per session. */
export function recordEngagedVisitIfNeeded(): void {
  if (shouldExcludeBetaMetrics()) return;
  if (hasSentEngagedVisit()) return;
  markEngagedVisitSent();
  recordBetaEvent("engaged_visit");
}

/** Page view + optional return-after-abandon (additive; never replaces page_view). */
export function recordPageView(): void {
  if (shouldExcludeBetaMetrics()) return;
  const visitor = getVisitorContext();
  recordBetaEvent("page_view", {
    visitor_id: visitor.visitorId,
    is_returning: visitor.isReturning,
  });
  if (takeReturnAfterAbandon(visitor.isReturning)) {
    recordBetaEvent("return_after_abandon");
  }
}

/**
 * Modal closed without signup. Always emits modal_abandon (existing counter);
 * dismiss_method is additive detail for pattern analysis.
 */
export function recordModalAbandon(dismissMethod: ModalDismissMethod): void {
  if (shouldExcludeBetaMetrics()) return;
  markModalAbandoned();
  recordBetaEvent("modal_abandon", { dismiss_method: dismissMethod });
}

/**
 * Module zoom — always emits module_zoom (existing attraction counter).
 * If this is the first zoom after abandon, also emits post_abandon_explore.
 */
export function recordModuleZoom(moduleId: string, surface: string = "section"): void {
  if (shouldExcludeBetaMetrics()) return;
  recordBetaEvent("module_zoom", { module_id: moduleId, surface });
  const postId = takePostAbandonExploreModuleId(moduleId);
  if (postId) {
    recordBetaEvent("post_abandon_explore", { module_id: postId, surface });
  }
}

export async function submitBetaWaitlist(payload: WaitlistPayload): Promise<WaitlistSubmitResult> {
  const base = apiBase();
  const excludeMetrics = shouldExcludeBetaMetrics();
  let response: Response;
  try {
    response = await fetch(base, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: payload.email,
        name: payload.name?.trim() || undefined,
        source: payload.source ?? "beta-page",
        website: payload.website ?? "",
        turnstileToken: payload.turnstileToken,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        elapsed_ms: getSessionElapsedMs(),
        had_module_zoom: getExploreSignupContext().hadModuleZoom,
        explored_modules_count: getExploreSignupContext().exploredModulesCount,
        visitor_id: getVisitorContext().visitorId,
        ...readCampaignAttribution(),
        // Staff browsers: still allow a real submit for testing, but do not bump funnel counts.
        ...(excludeMetrics ? { exclude_metrics: true } : {}),
      }),
    });
  } catch {
    return { status: "network" };
  }

  if (response.status === 201) return { status: "success" };
  if (response.status === 409) return { status: "duplicate" };
  if (response.status === 400) return { status: "validation" };
  if (response.status === 429) return { status: "throttle" };
  if (response.status === 403) return { status: "captcha" };
  return { status: "network" };
}

/** Public stats endpoint is admin-gated; retained for tooling that passes Basic auth. */
export async function fetchWaitlistStats(authHeader?: string): Promise<number | null> {
  const base = apiBase();
  try {
    const response = await fetch(`${base}stats`, {
      headers: authHeader ? { Authorization: authHeader } : {},
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { count?: number };
    return typeof data.count === "number" ? data.count : null;
  } catch {
    return null;
  }
}
